import React, { useState, useEffect } from 'react';
import { Event, Resource } from './types';
import { supabase } from '../../../lib/supabaseClient';
import { setHours, setMinutes, setSeconds, format as formatDateFns } from 'date-fns';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEvent: Event | null;
  resources: Resource[];
  modalDefaultDate: Date | null;
  onEventCreated?: (event: Event) => void;
  onEventUpdated?: (event: Event) => void;
  allEvents?: Event[];
}

const EventModal: React.FC<EventModalProps> = ({
  isOpen,
  onClose,
  currentEvent,
  resources,
  modalDefaultDate,
  onEventCreated,
  onEventUpdated,
  allEvents = []
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recurrenceType, setRecurrenceType] = useState<'none' | 'daily' | 'weekly' | 'monthly'>('none');

  useEffect(() => {
    if (currentEvent && currentEvent.isBlockedTime) {
    } else {
      setRecurrenceType('none');
    }
  }, [currentEvent]);

  // Helper function to convert Date to local datetime-local string format
  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Determine default start and end times
  let defaultStartStr = formatDateTimeLocal(new Date());
  let defaultEndStr = formatDateTimeLocal(new Date(new Date().getTime() + 60*60*1000));

  if (currentEvent?.start) {
    // Convert UTC timestamp to local time for datetime-local input
    defaultStartStr = formatDateTimeLocal(new Date(currentEvent.start));
  }
  if (currentEvent?.end) {
    // Convert UTC timestamp to local time for datetime-local input
    defaultEndStr = formatDateTimeLocal(new Date(currentEvent.end));
  }
  
  // If modalDefaultDate is provided (for new blocks from a specific day), it takes precedence over generic new Date()
  // but not over currentEvent (which means we are editing)
  if (modalDefaultDate && !currentEvent) { 
    defaultStartStr = formatDateFns(setSeconds(setMinutes(setHours(modalDefaultDate, 9), 0), 0), "yyyy-MM-dd'T'HH:mm");
    defaultEndStr = formatDateFns(setSeconds(setMinutes(setHours(modalDefaultDate, 10), 0), 0), "yyyy-MM-dd'T'HH:mm");
  }

  if (!isOpen) return null;

  // Check for scheduling conflicts
  const checkConflict = (start: Date, end: Date, resourceId: number, excludeEventId?: string | number): boolean => {
    return allEvents.some(e =>
      e.id !== excludeEventId &&
      !e.isBlockedTime &&
      e.resourceId === resourceId &&
      new Date(e.start) < end &&
      new Date(e.end) > start
    );
  };

  const handleSubmit = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const blockReasonInput = document.getElementById('block-reason') as HTMLInputElement;
      const startDateInput = document.getElementById('start-date') as HTMLInputElement;
      const endDateInput = document.getElementById('end-date') as HTMLInputElement;
      const resourceInput = document.getElementById('resource') as HTMLSelectElement;
      const notesInput = document.getElementById('notes') as HTMLTextAreaElement;
      const statusInput = document.getElementById('status') as HTMLSelectElement;
      const recurrenceTypeElement = document.getElementById('recurrence-type') as HTMLSelectElement;
      const selectedRecurrenceType = recurrenceTypeElement ? recurrenceTypeElement.value as typeof recurrenceType : 'none';

      if (!startDateInput.value || !endDateInput.value) {
        throw new Error('Start and End date & time are required');
      }

      const localStart = new Date(startDateInput.value);
      const localEnd = new Date(endDateInput.value);

      if (localStart >= localEnd) {
        throw new Error('End time must be after start time');
      }

      const resource = resources.find(r => r.id === parseInt(resourceInput.value));
      if (!resource || !resource.staffId) {
        throw new Error('Selected resource does not have a valid staff ID.');
      }
      const staffUuid = resource.staffId;

      // Check if we're editing an appointment or blocked time
      const isAppointment = currentEvent && !currentEvent.isBlockedTime;

      // Check for conflicts when editing appointment
      if (isAppointment) {
        const hasConflict = checkConflict(
          localStart,
          localEnd,
          parseInt(resourceInput.value),
          currentEvent.id
        );

        if (hasConflict) {
          throw new Error('This time slot conflicts with another appointment for the selected staff member. Please choose a different time.');
        }
      }

      if (isAppointment) {
        // Handle appointment update
        const utcStartISO = localStart.toISOString().replace('Z', '');
        const utcEndISO = localEnd.toISOString().replace('Z', '');

        const appointmentData = {
          start_time: utcStartISO,
          end_time: utcEndISO,
          staff_id: staffUuid,
          notes: notesInput.value || currentEvent.notes || '',
          status: statusInput?.value || currentEvent.status || 'scheduled'
        };

        const { data, error: dbError } = await supabase
          .from('appointments')
          .update(appointmentData)
          .eq('id', currentEvent.id)
          .select()
          .single();

        if (dbError) {
          console.error('Supabase error updating appointment:', dbError);
          throw new Error(`Error updating appointment: ${dbError.message}`);
        }

        if (!data) {
          throw new Error('Failed to update appointment: No record returned.');
        }

        const displayEventData: Event = {
          id: data.id,
          title: currentEvent.title,
          start: new Date(data.start_time + 'Z').toISOString(),
          end: new Date(data.end_time + 'Z').toISOString(),
          resourceId: parseInt(resourceInput.value),
          status: data.status as Event['status'],
          client: currentEvent.client || '',
          notes: data.notes || '',
          recurring: false,
          isBlockedTime: false,
          services: currentEvent.services,
          clientEmail: currentEvent.clientEmail,
          clientPhone: currentEvent.clientPhone,
          paymentStatus: currentEvent.paymentStatus,
          appointmentSource: currentEvent.appointmentSource,
          totalAmount: currentEvent.totalAmount
        };

        if (onEventUpdated) {
          onEventUpdated(displayEventData);
        }

        onClose();
        return;
      }

      // Handle blocked time (existing logic)
      const utcStart = new Date(localStart.toISOString());
      const utcEnd = new Date(localEnd.toISOString());

      const dateForDb = utcStart.toISOString().split('T')[0];
      const startTimeForDb = utcStart.toISOString().split('T')[1].substring(0, 8);
      const endTimeForDb = utcEnd.toISOString().split('T')[1].substring(0, 8);
            
      const blockDataForSupabase = {
        staff_id: staffUuid,
        specific_date: dateForDb,
        start_time: startTimeForDb,
        end_time: endTimeForDb,
        is_recurring: selectedRecurrenceType !== 'none',
        notes: blockReasonInput.value || notesInput.value || 'Blocked Time'
      };
      
      let newBlockRecord;
      
      if (currentEvent) {
        // Update existing event
        const { data, error: dbError } = await supabase
          .from('staff_availability')
          .update(blockDataForSupabase)
          .eq('id', currentEvent.id)
          .select()
          .single();
          
        if (dbError) {
          console.error('Supabase error updating blocked time:', dbError);
          throw new Error(`Error updating blocked time: ${dbError.message}`);
        }
        
        newBlockRecord = data;
      } else {
        // Insert new event
        const { data, error: dbError } = await supabase
          .from('staff_availability')
          .insert(blockDataForSupabase)
          .select()
          .single();
          
        if (dbError) {
          console.error('Supabase error inserting blocked time:', dbError);
          throw new Error(`Error blocking time: ${dbError.message}`);
        }
        
        newBlockRecord = data;
      }

      if (!newBlockRecord) {
        console.error('No record returned from Supabase after operation.');
        throw new Error('Failed to save blocked time: No record returned.');
      }
      
      // newBlockRecord.specific_date, .start_time, .end_time are already UTC parts
      const eventStartStr = `${newBlockRecord.specific_date}T${newBlockRecord.start_time}Z`;
      const eventEndStr = `${newBlockRecord.specific_date}T${newBlockRecord.end_time}Z`;

      const displayEventData: Event = {
        id: newBlockRecord.id,
        title: newBlockRecord.notes || 'Blocked Time',
        start: eventStartStr,
        end: eventEndStr,
        resourceId: parseInt(resourceInput.value),
        status: 'cancelled',
        client: '',
        notes: newBlockRecord.notes || '',
        recurring: newBlockRecord.is_recurring || false,
        isBlockedTime: true
      };
      
      // Use the callback to update state in parent without requiring a fetch
      if (currentEvent && onEventUpdated) {
        onEventUpdated(displayEventData);
      } else if (onEventCreated) {
        onEventCreated(displayEventData);
      }
      
      onClose();

    } catch (err: any) {
      setError(err.message || 'An error occurred');
      console.error('Error handling block time event:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            {currentEvent && !currentEvent.isBlockedTime
              ? 'Edit Appointment'
              : currentEvent
              ? 'Edit Blocked Time'
              : 'Block Staff Time'}
          </h3>
          <button
            className="text-gray-400 hover:text-gray-500"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        
        <div className="p-6">
          <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
            {currentEvent && !currentEvent.isBlockedTime && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                <h4 className="font-medium text-blue-900 mb-2">Appointment Information</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><span className="font-medium">Client:</span> {currentEvent.client}</p>
                  {currentEvent.services && currentEvent.services.length > 0 && (
                    <p><span className="font-medium">Services:</span> {currentEvent.services.map((s: any) => s.name).join(', ')}</p>
                  )}
                  {currentEvent.paymentStatus && (
                    <p><span className="font-medium">Payment:</span> {currentEvent.paymentStatus.replace('_', ' ')}</p>
                  )}
                </div>
              </div>
            )}

            {(!currentEvent || currentEvent.isBlockedTime) && (
              <div>
                <label htmlFor="block-reason" className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Block (Optional)
                </label>
                <input
                  type="text"
                  id="block-reason"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  defaultValue={currentEvent && currentEvent.isBlockedTime ? currentEvent.title : ''}
                />
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date & Time
                </label>
                <input
                  type="datetime-local"
                  id="start-date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  defaultValue={defaultStartStr}
                />
              </div>
              
              <div>
                <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  id="end-date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  defaultValue={defaultEndStr}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="resource" className="block text-sm font-medium text-gray-700 mb-1">
                Resource (Staff)
              </label>
              <select
                id="resource"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                defaultValue={currentEvent?.resourceId || (resources[0]?.id || '')}
              >
                {resources.map(resource => (
                  <option key={resource.id} value={resource.id}>
                    {resource.name}
                  </option>
                ))}
              </select>
            </div>

            {currentEvent && !currentEvent.isBlockedTime && (
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Appointment Status
                </label>
                <select
                  id="status"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  defaultValue={currentEvent.status || 'scheduled'}
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="pending_payment">Pending Payment</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="no_show">No Show</option>
                </select>
              </div>
            )}

            {(!currentEvent || currentEvent.isBlockedTime) && (
              <div>
                <label htmlFor="recurrence-type" className="block text-sm font-medium text-gray-700 mb-1">
                  Recurrence
                </label>
                <select
                  id="recurrence-type"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  value={recurrenceType}
                  onChange={(e) => setRecurrenceType(e.target.value as typeof recurrenceType)}
                >
                  <option value="none">Does not repeat</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            )}
            
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes (Optional)
              </label>
              <textarea
                id="notes"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                defaultValue={currentEvent?.notes || ''}
              ></textarea>
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                <p>Error: {error}</p>
              </div>
            )}
            
            <div className="pt-1 border-t clear-both"></div>

            <div className="flex justify-end space-x-3 pt-0">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
      {/* Button to update event status */}
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-[#3A5E63] border border-transparent rounded-md shadow-sm hover:bg-[#2c474b] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A5E63]"
                disabled={isLoading}
              >
                {isLoading
                  ? 'Saving...'
                  : currentEvent && !currentEvent.isBlockedTime
                  ? 'Update Appointment'
                  : currentEvent
                  ? 'Update'
                  : 'Block Time'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventModal; 